import clsx from "clsx";
import { ChevronRight, Crosshair, RotateCcw, Ruler } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { MenuItem, MenuSeparator } from "../../components/Menu";
import { Motion, Presence, useClosing } from "../../components/presence";
import { Kbd } from "../../components/ui";
import { useDismiss } from "../../components/useDismiss";
import { t } from "../../i18n/i18n";
import { unitMissionName } from "../../i18n/vocabulary";
import { anchorOf, MissionThumb, missionImageOf, requestMissionMenu } from "../../panels/MissionMenu";
import { standingTaskLabel } from "../../panels/missionMenuModel";
import { useUi } from "../../state/ui";
import { symbolSvg } from "./symbols";
import { useDeployment } from "./useDeployment";
import { useUnitHover } from "./UnitHover";
import { type UnitMenuRequest, useUnitMenu } from "./useUnitMenu";
import { useUnitMoves } from "./useUnitMoves";
import { refuseIfDossierLocked } from "../../sharing/access";

/** Menu d'une unité sur la carte (clic droit, appui long au doigt ; docs/14 §2.1) : mission du temps affiché (ouvre
 *  le menu de mission), « Revenir à la position doctrinale », « Centrer », « Mesurer depuis cette unité ». Entrées
 *  du menu partagé (`MenuItem`) ; sans dossier enregistré, elles sont bloquées et leur clic désigne
 *  l'enregistrement. Rendu une fois par
 *  l'application, au point du pointeur ; la carte écrit la demande dans `useUnitMenu`. */
export function UnitContextMenu() {
  const request = useUnitMenu((s) => s.request);
  return createPortal(
    <Presence show={Boolean(request)}>
      {request && <Menu key={`${request.unit}|${request.phase}|${request.x}|${request.y}`} request={request} />}
    </Presence>,
    document.body,
  );
}

const WIDTH = 248;
const MARGIN = 8;

function Menu({ request }: { request: UnitMenuRequest }) {
  const close = useUnitMenu((s) => s.close);
  const requestSave = useUi((s) => s.requestSave);
  const notify = useUi((s) => s.notify);
  const flyTo = useUi((s) => s.requestFlyTo);
  const { data: deployment } = useDeployment();
  const { canMove, reset, busy } = useUnitMoves();
  const ref = useRef<HTMLDivElement>(null);
  const closing = useClosing();
  useDismiss(!closing, close, ref);
  const unit = deployment?.units.find((u) => u.key === request.unit);
  const position = unit?.positions[request.phase];

  // Fenêtre redimensionnée, carte zoomée (molette, pincement) ou déplacée (glisser au doigt) : le menu, posé au
  // point du clic, se ferme au lieu de flotter loin de son unité. Les gestes dans le menu lui-même ne comptent pas.
  useEffect(() => {
    const outside = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    window.addEventListener("resize", close);
    window.addEventListener("wheel", outside, { capture: true, passive: true });
    window.addEventListener("touchmove", outside, { capture: true, passive: true });
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("wheel", outside, { capture: true });
      window.removeEventListener("touchmove", outside, { capture: true });
    };
  }, [close]);
  // Unité absente à ce temps (changement de temps, de dossier) : rien à montrer.
  useEffect(() => {
    if (deployment && !position) close();
  }, [deployment, position, close]);
  // Premier élément au clavier, comme un menu natif.
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus({ preventScroll: true });
  }, []);

  const [where] = useState(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(MARGIN, Math.min(request.x, vw - WIDTH - MARGIN));
    const down = vh - request.y > 260 || request.y < vh / 2;
    return down
      ? { left, top: Math.min(request.y + 2, vh - MARGIN - 120), origin: "top left" }
      : { left, bottom: vh - request.y + 2, origin: "bottom left" };
  });

  if (!unit || !position) return null;
  const name = unit.display_name || unit.designation;
  const mission = unitMissionName(position.mission_label || position.mission) || t("Sans mission");
  const standing = standingTaskLabel(unit);
  const saveFirst = (text: string) => {
    close();
    // Dossier en lecture seule pour le compte (docs/17 §3.2) : le bandeau est désigné, pas l'enregistrement.
    if (refuseIfDossierLocked()) return;
    requestSave();
    notify({ text, tone: "info" });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = [...(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
  };

  return (
    <Motion
      ref={ref}
      origin="zoom"
      role="menu"
      aria-label={t("Menu de {unit}", { unit: name })}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
      className="fixed z-[100] rounded-xl border border-line bg-surface p-1 shadow-float"
      style={{
        left: where.left,
        width: WIDTH,
        transformOrigin: where.origin,
        ...(where.top !== undefined ? { top: where.top } : { bottom: where.bottom }),
      }}
    >
      <div className="flex h-8 items-center gap-2 px-2">
        <span
          className="flex h-4 w-6 shrink-0 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6"
          dangerouslySetInnerHTML={{ __html: symbolSvg(unit.sidc, 13) }}
        />
        <span className="min-w-0 flex-1 truncate text-ui font-semibold text-ink" title={unit.designation}>
          {name}
        </span>
        <span className="shrink-0 text-meta font-medium text-ink-muted tabular-nums">{request.phase}</span>
      </div>
      <MenuSeparator />
      {unit.mission_editable ? (
        <button
          type="button"
          role="menuitem"
          aria-haspopup="menu"
          aria-disabled={canMove ? undefined : true}
          onClick={() => {
            if (!canMove) return saveFirst(t("Enregistrez le dossier pour changer la mission."));
            close();
            requestMissionMenu(unit, request.phase, anchorOf({ x: where.left, y: request.y }));
          }}
          title={t("Changer la mission en {phase}", { phase: request.phase })}
          className={clsx(
            "flex min-h-10 w-full items-center gap-2.5 rounded-md px-2 py-1 text-left transition-colors",
            canMove ? "hover:bg-hover focus-visible:bg-hover" : "opacity-40",
          )}
        >
          <MissionThumb image={missionImageOf(deployment, unit, position.mission)} label={mission} />
          <span className="min-w-0 flex-1">
            <span className="block text-meta leading-tight text-ink-muted">{t("Mission en {phase}", { phase: request.phase })}</span>
            <span className="block truncate text-ui font-medium text-ink">{mission}</span>
          </span>
          {position.overridden && <span className="size-1.5 shrink-0 rounded-full bg-accent" title={t("Mission choisie à la main")} />}
          <ChevronRight size={14} className="shrink-0 text-ink-muted" />
        </button>
      ) : standing ? (
        // PC, trains de combat (A95) : ni mission ni menu, la tâche permanente et le lieu du temps.
        <div className="flex min-h-10 items-center gap-2.5 px-2 py-1">
          <span className="min-w-0 flex-1">
            <span className="block text-meta leading-tight text-ink-muted">{standing}</span>
            <span className="block truncate text-ui font-medium text-ink">{position.locality || t("Lieu-dit sans nom")}</span>
          </span>
        </div>
      ) : (
        <div className="flex min-h-10 items-center gap-2.5 px-2 py-1">
          <MissionThumb image={missionImageOf(deployment, unit, position.mission)} label={mission} />
          <span className="min-w-0 flex-1">
            <span className="block text-meta leading-tight text-ink-muted">{t("Mission en {phase}", { phase: request.phase })}</span>
            <span className="block truncate text-ui font-medium text-ink">{mission}</span>
          </span>
        </div>
      )}
      <MenuSeparator />
      <MenuItem
        icon={RotateCcw}
        label={t("Revenir à la position doctrinale")}
        disabled={canMove && (!position.moved || busy)}
        blocked={!canMove}
        onBlocked={() => saveFirst(t("Enregistrez le dossier pour déplacer les unités."))}
        onSelect={() => {
          close();
          reset.mutate({ unit: unit.key, phase: request.phase });
        }}
      />
      <MenuItem
        icon={Crosshair}
        label={t("Centrer")}
        onSelect={() => {
          close();
          flyTo([position.lon, position.lat], 13);
        }}
      />
      <MenuItem
        icon={Ruler}
        label={t("Mesurer depuis cette unité")}
        onSelect={() => {
          close();
          useUnitHover.getState().arm(unit.key);
        }}
        trailing={<Kbd hideOnTouch>M</Kbd>}
      />
    </Motion>
  );
}
